/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsSaveFilters } from "../funcs/dashboardsSaveFilters.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SaveDashboardFiltersRequest,
  SaveDashboardFiltersSecurity,
} from "../models/operations/savedashboardfilters.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SaveDashboardFiltersMutationVariables = {
  request: SaveDashboardFiltersRequest;
  security?: SaveDashboardFiltersSecurity | undefined;
  options?: RequestOptions;
};

export type SaveDashboardFiltersMutationData = Dashboard;

export type SaveDashboardFiltersMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * saveDashboardFilters dashboards
 *
 * @remarks
 * Store the date range and filter values a dashboard opens on, for everyone. Until saved, changes in the filter bar are the viewer's own.
 */
export function useSaveDashboardFiltersMutation(
  options?: MutationHookOptions<
    SaveDashboardFiltersMutationData,
    SaveDashboardFiltersMutationError,
    SaveDashboardFiltersMutationVariables
  >,
): UseMutationResult<
  SaveDashboardFiltersMutationData,
  SaveDashboardFiltersMutationError,
  SaveDashboardFiltersMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSaveDashboardFiltersMutation(client, options),
    ...options,
  });
}

export function mutationKeySaveDashboardFilters(): MutationKey {
  return ["@gram/client", "dashboards", "saveFilters"];
}

export function buildSaveDashboardFiltersMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SaveDashboardFiltersMutationVariables,
  ) => Promise<SaveDashboardFiltersMutationData>;
} {
  return {
    mutationKey: mutationKeySaveDashboardFilters(),
    mutationFn: function saveDashboardFiltersMutationFn({
      request,
      security,
      options,
    }): Promise<SaveDashboardFiltersMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsSaveFilters(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
